import { FlightRecommendationSearchSortingType, SearchCountry, SearchFlightRecommendationSortItem, SearchGender, SearchHotelTransfer, SearchTravelSortItem } from "./../../types/models";
import {
  SearchFlightRecommendationFilterItem,
  SearchHotelTransfersResponse,
  SearchBookingOverview,
  SearchHotelRoom,
  SearchTravelPriceResponse,
  SearchHotelPension,
  SearchFlightRecommendationResponse,
  SearchHotelRoomsResponse,
  SearchTravelBookingSelection,
  SearchHotelPensionResponse,
  SearchFlightRecommendation,
} from "@/types/models";
import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import TravelService from "@/services/travel.service";
import {
  ValueIndexParams,
  MultiRangeValues,
  UpdateSearchPerson,
  SearchFlightRecommendationFilterItemType,
} from "@/types/paramModels";

@Module({ namespaced: true })
class TravelSearchBookingModule extends VuexModule {
  public searchTravelBookingOverview?: SearchBookingOverview;
  public bookingRooms?: SearchHotelRoomsResponse;
  public pensions?: SearchHotelPensionResponse;
  public flightRecommendations?: SearchFlightRecommendationResponse;
  public transfers?: SearchHotelTransfersResponse;

  @Mutation
  public setSearchTravelBookingOverview(
    searchTravelBookingOverview: SearchBookingOverview
  ): void {
    this.searchTravelBookingOverview = searchTravelBookingOverview;
  }

  @Action
  public updateSearchTravelBookingOverview(
    searchTravelBookingOverview: SearchBookingOverview
  ): void {
    this.context.commit(
      "setSearchTravelBookingOverview",
      searchTravelBookingOverview
    );
  }

  get getSearchTravelBookingOverview(): SearchBookingOverview {
    return this.searchTravelBookingOverview as SearchBookingOverview;
  }

  @Mutation
  private setDeleteflightRecommendations(): void {
    if (this.flightRecommendations)
      this.flightRecommendations = {} as SearchFlightRecommendationResponse;
  }

  @Mutation
  private setDeleteBookingRooms(): void {
    if (this.bookingRooms) this.bookingRooms = {} as SearchHotelRoomsResponse;
  }

  @Mutation
  private setDeletePensions(): void {
    if (this.pensions) this.pensions = {} as SearchHotelPensionResponse;
  }

  @Mutation
  private setDeleteSearchTravelBookingOverview(): void {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview = {} as SearchBookingOverview;
  }

  @Mutation
  private setDeleteTransfers(): void {
    if (this.transfers) this.transfers = {} as SearchHotelTransfersResponse;
  }

  @Action
  public deleteSearchTravelBookingOverview(): void {
    this.context.commit("setDeleteSearchTravelBookingOverview");
    this.context.commit("setDeleteBookingRooms");
    this.context.commit("setDeleteflightRecommendations");
    this.context.commit("setDeleteTransfers");
  }

  @Mutation
  private setSearchKey(key: string) {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.searchKey = key;
  }

  @Action
  public updateSearchKey(key: string) {
    this.context.commit("setSearchKey", key);
  }

  @Mutation
  private setFlightsSearchKey(key: string) {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.flightsSearchKey = key;
  }

  @Action
  public updateFlightsSearchKey(key: string) {
    this.context.commit("setFlightsSearchKey", key);
  }

  @Mutation
  private setHotelsSearchKey(key: string) {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.hotelsSearchKey = key;
  }

  @Action
  public updateHotelsSearchKey(key: string) {
    this.context.commit("setHotelsSearchKey", key);
  }

  @Mutation
  private setHotelCode(code: string) {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.hotelCode = code;
  }

  @Action
  public updateHotelCode(code: string) {
    this.context.commit("setHotelCode", code);
  }

  @Mutation
  private setHotelTransferKey(key: string) {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.hotelTransferKey = key;
  }

  @Action
  public updateHotelTransferKey(key: string) {
    this.context.commit("setHotelTransferKey", key);
  }

  @Mutation
  private setFlightId(flightId: string) {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.flightId = flightId;
  }

  @Action
  public updateFlightId(flightId: string) {
    this.context.commit("setFlightId", flightId);
  }

  @Mutation
  private setUpdatedBookingSelection(
    bookingSelection: SearchTravelBookingSelection
  ): void {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection = bookingSelection;
  }

  @Action
  public updateBookingSelection(
    bookingSelection: SearchTravelBookingSelection
  ): void {
    this.context.commit("setUpdatedBookingSelection", bookingSelection);
  }

  @Mutation
  private setCalculatedPrice(price: SearchTravelPriceResponse): void {
    if (this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.price = price;
  }

  @Action
  public updateCalculatedPrice(
    bookingSelection: SearchTravelBookingSelection
  ): void {
    TravelService.getSearchCalculatedPrice(bookingSelection).then(
      (response) => {
        if (response.data.success) {
          this.context.commit("setCalculatedPrice", response.data.result);
        } else {
          console.warn("Call for calculated price failed");
        }
      }
    );
  }

  @Mutation
  private setBookingRooms(rooms: SearchHotelRoomsResponse): void {
    this.bookingRooms = rooms;
  }

  @Action
  public updateBookingRooms(rooms: SearchHotelRoomsResponse): void {
    this.context.commit("setBookingRooms", rooms);
  }

  get getBookingRooms(): SearchHotelRoomsResponse {
    return this.bookingRooms as SearchHotelRoomsResponse;
  }

  @Mutation
  private async setUpdatedBookingRoom(params: ValueIndexParams) {
    if (this.searchTravelBookingOverview) {
      const newRoom = params.value as SearchHotelRoom;
      for (const room of this.searchTravelBookingOverview.paxRooms[params.index]
        .rooms) {
        if (room.hotelRoomKey === newRoom.hotelRoomKey) room.isSelected = true;
        else room.isSelected = false;
      }
      this.searchTravelBookingOverview.bookingSelection.hotelRooms[
        params.index
      ].hotelRoomKey = newRoom.hotelRoomKey;
    }
  }

  @Action
  private async bookingRoomUpdate(params: ValueIndexParams) {
    await this.context.commit("setUpdatedBookingRoom", params);
    const selectedPension = params.value.pensions.find(
      (pension) => pension.isSelected
    );
    const pensionParams = {
      index: params.index,
      value: selectedPension,
    } as ValueIndexParams;
    this.context.commit("setUpdatedPension", pensionParams);
  }

  @Action
  public async updateBookingRoom(params: ValueIndexParams) {
    await this.context.dispatch("bookingRoomUpdate", params);
    this.context.dispatch(
      "updateCalculatedPrice",
      this.searchTravelBookingOverview?.bookingSelection
    );
  }

  @Mutation
  private setPensions(pensions: SearchHotelPensionResponse): void {
    this.pensions = pensions;
  }

  @Action
  public updatePensions(pensions: SearchHotelRoomsResponse): void {
    this.context.commit("setPensions", pensions);
  }

  get getPensions(): SearchHotelPensionResponse {
    return this.pensions as SearchHotelPensionResponse;
  }

  @Mutation
  private setUpdatedPension(params: ValueIndexParams): void {
    if (this.searchTravelBookingOverview) {
      const newPension = params.value as SearchHotelPension;
      const selectedRoom = this.searchTravelBookingOverview.paxRooms[
        params.index
      ].rooms.find((room) => room.isSelected);
      if (selectedRoom) {
        for (const pension of selectedRoom.pensions) {
          if (pension.key === newPension.key) pension.isSelected = true;
          else pension.isSelected = false;
        }
      }
      this.searchTravelBookingOverview.bookingSelection.hotelRooms[
        params.index
      ].hotelPensionKey = newPension.key;
    }
  }

  @Action
  private async pensionUpdate(params: ValueIndexParams) {
    this.context.commit("setUpdatedPension", params);
  }

  @Action
  public async updatePension(params: ValueIndexParams) {
    await this.context.dispatch("pensionUpdate", params);
    this.context.dispatch(
      "updateCalculatedPrice",
      this.searchTravelBookingOverview?.bookingSelection
    );
  }

  @Mutation
  private setFlightRecommendations(
    flightRecommendations: SearchFlightRecommendationResponse
  ): void {
    this.flightRecommendations = flightRecommendations;
  }

  @Action
  public updateFlightRecommendations(
    flightRecommendations: SearchFlightRecommendationResponse
  ): void {
    this.context.commit("setFlightRecommendations", flightRecommendations);
  }

  @Mutation
  private setUpdatedFilteredFlights(
    filteredFlights: SearchFlightRecommendationResponse
  ) {
    if (this.flightRecommendations) {
      this.flightRecommendations.paging = filteredFlights.paging;
      this.flightRecommendations.flights = filteredFlights.flights;
    }
  }

  @Action
  public updateFilteredFlights(
    filteredFlights: SearchFlightRecommendationResponse
  ) {
    this.context.commit("setUpdatedFilteredFlights", filteredFlights);
  }

  get getflightRecommendations(): SearchFlightRecommendationResponse {
    return this.flightRecommendations as SearchFlightRecommendationResponse;
  }

  @Mutation
  private setIncrementFlightRecommendationsPaging(): void {
    if (this.flightRecommendations)
      this.flightRecommendations.paging.pageNumber++;
  }

  @Action
  public incrementFlightRecommendationsPaging(): void {
    this.context.commit("setIncrementFlightRecommendationsPaging");
  }

  @Mutation
  private setResetFlightRecommendationsPaging() {
    if (this.flightRecommendations)
      this.flightRecommendations.paging.pageNumber = 1;
  }

  @Action resetFlightRecommendationsPaging() {
    this.context.commit("setResetFlightRecommendationsPaging");
  }

  @Mutation
  private setAddFlightRecommendationsPage(
    flightRecommendations: Array<SearchFlightRecommendation>
  ) {
    if (this.flightRecommendations) {
      for (const flight of flightRecommendations) {
        this.flightRecommendations.flights.push(flight);
      }
    }
  }

  @Action
  public addFlightRecommendationsPage(
    flightRecommendations: Array<SearchFlightRecommendation>
  ) {
    this.context.commit(
      "setAddFlightRecommendationsPage",
      flightRecommendations
    );
  }

  @Mutation
  private async setUpdatedSelectedFlight(flight: SearchFlightRecommendation) {
    if (this.flightRecommendations) {
      this.flightRecommendations.bookingSelection.flightId = flight.id;
    }
    if (this.searchTravelBookingOverview) {
      this.searchTravelBookingOverview.flight = flight;
      this.searchTravelBookingOverview.bookingSelection.flightId = flight.id;
    }
  }

  @Action
  public async updateSelectedFlight(flight: SearchFlightRecommendation) {
    await this.context.commit("setUpdatedSelectedFlight", flight);
    this.context.dispatch(
      "updateCalculatedPrice",
      this.searchTravelBookingOverview?.bookingSelection
    );
  }

  @Mutation
  private setUpdatedFilterMaxStops(
    stop: SearchFlightRecommendationFilterItem
  ): void {
    if (this.flightRecommendations)
      this.flightRecommendations.searchFilter.maxStops.forEach((filterStop) => {
        if (filterStop.id === stop.id) filterStop.isSelected = true;
        else filterStop.isSelected = false;
      });
  }

  @Action
  public updateFilterMaxStops(
    stop: SearchFlightRecommendationFilterItem
  ): void {
    this.context.commit("setUpdatedFilterMaxStops", stop);
  }

  @Mutation
  private async setResetFilterMaxStops() {
    if (this.flightRecommendations) {
      this.flightRecommendations.searchFilter.maxStops.forEach((filterStop) => {
        filterStop.isSelected = false;
      });
    }
  }

  @Action
  public async resetFilterMaxStops() {
    this.context.commit("setResetFilterMaxStops");
  }

  @Mutation
  private setRemoveFilterAirline(
    airline: SearchFlightRecommendationFilterItem
  ): void {
    if (this.flightRecommendations) {
      for (const filterAirline of this.flightRecommendations.searchFilter
        .airlines) {
        if (filterAirline.id === airline.id) {
          filterAirline.isSelected = false;
          break;
        }
      }
    }
  }

  @Mutation
  private setUpdatedFilterAirline(params: ValueIndexParams): void {
    if (this.flightRecommendations)
      this.flightRecommendations.searchFilter.airlines[
        params.index
      ].isSelected = params.value;
  }

  @Action
  public updateFilterAirline(params: ValueIndexParams): void {
    this.context.commit("setUpdatedFilterAirline", params);
  }

  @Mutation
  private async setResetFilterAirlines() {
    if (this.flightRecommendations?.searchFilter.airlines) {
      this.flightRecommendations.searchFilter.airlines.forEach((airline) => {
        airline.isSelected = false;
      });
    }
  }

  @Action
  public resetFilterAirlines() {
    this.context.commit("setResetFilterAirlines");
  }

  @Mutation
  private setRemoveFilterTravelClass(
    travelClass: SearchFlightRecommendationFilterItem
  ): void {
    if (this.flightRecommendations) {
      for (const filterTravelClass of this.flightRecommendations.searchFilter
        .travelClass) {
        if (filterTravelClass.id === travelClass.id) {
          filterTravelClass.isSelected = false;
          break;
        }
      }
    }
  }

  @Mutation
  private setUpdatedFilterTravelClass(params: ValueIndexParams): void {
    if (this.flightRecommendations)
      this.flightRecommendations.searchFilter.travelClass[
        params.index
      ].isSelected = params.value;
  }

  @Action
  public updateFilterTravelClass(params: ValueIndexParams): void {
    this.context.commit("setUpdatedFilterTravelClass", params);
  }

  @Mutation
  private async setResetFilterTravelClasses() {
    if (this.flightRecommendations?.searchFilter.travelClass) {
      this.flightRecommendations.searchFilter.travelClass.forEach(
        (travelClass) => {
          travelClass.isSelected = false;
        }
      );
    }
  }

  @Action
  public resetFilterTravelClasses() {
    this.context.commit("setResetFilterTravelClasses");
  }

  @Mutation
  private async resetFilterMinTravelTime() {
    if (this.flightRecommendations)
      this.flightRecommendations.searchFilter.minSliderValue =
        this.flightRecommendations.searchFilter.minSliderValueAvailable;
  }

  @Mutation
  private resetFilterMaxTravelTime(): void {
    if (this.flightRecommendations)
      this.flightRecommendations.searchFilter.maxSliderValue =
        this.flightRecommendations.searchFilter.maxSliderValueAvailable;
  }

  @Mutation
  private setUpdatedFilterMinTravelLength(minValue: number): void {
    if (this.flightRecommendations)
      this.flightRecommendations.searchFilter.minSliderValue = minValue;
  }

  @Mutation
  private setUpdatedFilterMaxTravelLength(maxValue: number): void {
    if (this.flightRecommendations)
      this.flightRecommendations.searchFilter.maxSliderValue = maxValue;
  }

  @Action
  public updateFilterMinTravelLength(minValue: number): void {
    this.context.commit("setUpdatedFilterMinTravelLength", minValue);
  }

  @Action
  public updateFilterMaxTravelLength(maxValue: number): void {
    this.context.commit("setUpdatedFilterMaxTravelLength", maxValue);
  }

  @Action
  public updateFilterTravelLength(values: MultiRangeValues): void {
    this.context.commit("setUpdatedFilterMinTravelLength", values.minValue);
    this.context.commit("setUpdatedFilterMaxTravelLength", values.maxValue);
  }

  @Action
  public updateFilterFromTags(tag: SearchFlightRecommendationFilterItemType) {
    switch (tag.type) {
      case "maxStop":
        this.context.commit("setResetFilterMaxStops");
        break;
      case "airline":
        this.context.commit("setRemoveFilterAirline", tag);
        break;
      case "travelClass":
        this.context.commit("setRemoveFilterTravelClass", tag);
        break;
      case "minSliderValue":
        this.context.commit("resetFilterMinTravelTime");
        break;
      case "maxSliderValue":
        this.context.commit("resetFilterMaxTravelTime");
        break;
      default:
        break;
    }
  }

  @Action
  public async resetFilterAll() {
    await this.context.commit('setResetFilterMaxStops');
    await this.context.commit('setResetFilterAirlines');
    await this.context.commit('setResetFilterTravelClasses');
    await this.context.commit("resetFilterMinTravelTime");
    this.context.commit("resetFilterMaxTravelTime");
  }

  @Mutation
  private setUpdatedFilterSort(sortItem: SearchFlightRecommendationSortItem) {
    if (this.flightRecommendations) {
      this.flightRecommendations.paging.pageNumber = 1;
      for (const item of this.flightRecommendations.sorting.items) {
        if (item.id === sortItem.id) {
          item.selected = true;
        } else {
          item.selected = false;
        }
      }
    }
  }

  @Action
  public updateFilterSort(sortItem: SearchFlightRecommendationSortItem) {
    this.context.commit('setUpdatedFilterSort', sortItem);
  }

  @Mutation
  public async setTransfers(transfers: SearchHotelTransfersResponse) {
    this.transfers = transfers;
  }

  @Action
  public async updateTransfers(transfers: SearchHotelTransfersResponse) {
    await this.context.commit("setTransfers", transfers);
    this.context.dispatch(
      "updateCalculatedPrice",
      this.searchTravelBookingOverview?.bookingSelection
    );
  }

  get getTransfers(): SearchHotelTransfersResponse {
    return this.transfers as SearchHotelTransfersResponse;
  }

  @Mutation
  private async setUpdatedTransfer(selectedTransfer: SearchHotelTransfer) {
    if (this.searchTravelBookingOverview) {
      this.searchTravelBookingOverview.bookingSelection.hotelTransferKey =
        selectedTransfer.hotelTransferKey;
      for (const transfer of this.searchTravelBookingOverview.transfers) {
        if (transfer.hotelTransferKey === selectedTransfer.hotelTransferKey)
          transfer.isSelected = true;
        else transfer.isSelected = false;
      }
    }
  }

  @Action
  public async updateTransfer(transfer: SearchHotelTransfer) {
    await this.context.commit("setUpdatedTransfer", transfer);
    this.context.dispatch(
      "updateCalculatedPrice",
      this.searchTravelBookingOverview?.bookingSelection
    );
  }

  @Mutation
  private setUpdatedPersonProp(params: UpdateSearchPerson) {
    if (this.searchTravelBookingOverview) {
      if (params.isAdult)
        this.searchTravelBookingOverview.bookingSelection.hotelRooms[
          params.hotelRoomIndex
        ].searchRoom.adults[params.personIndex][params.propName] = params.value;
      else
        this.searchTravelBookingOverview.bookingSelection.hotelRooms[
          params.hotelRoomIndex
        ].searchRoom.children[params.personIndex][params.propName] =
          params.value;
    }
  }

  @Action
  public updatePersonProp(params: UpdateSearchPerson) {
    this.context.commit("setUpdatedPersonProp", params);
  }

  @Mutation
  private setContactPersonCountry(country: SearchCountry) {
    if(this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.hotelRooms[0].searchRoom.adults[0].country = country;
  }

  @Action
  public updateContactPersonCountry(country: SearchCountry) {
    this.context.commit('setContactPersonCountry', country);
  }

  @Mutation
  private setUpdatedReceiptEmail(params: ValueIndexParams) {
    if (this.searchTravelBookingOverview) {
      this.searchTravelBookingOverview.bookingSelection.receiptEmails[params.index] = params.value;
    }
  }

  @Action
  public updateReceiptEmail(params: ValueIndexParams) {
    this.context.commit("setUpdatedReceiptEmail", params);
  }

  @Mutation
  private setDeleteReceiptEmail(index: number) {
    if(this.searchTravelBookingOverview)
      this.searchTravelBookingOverview.bookingSelection.receiptEmails.splice(index, 1);
  }

  @Action
  public deleteReceiptEmail(index: number) {
    this.context.commit("setDeleteReceiptEmail", index);
  }

  get getReceiptEmails(): Array<string> {
    return this.searchTravelBookingOverview?.bookingSelection.receiptEmails as Array<string>;
  }
}

export default TravelSearchBookingModule;
